import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  Firestore 
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../hooks/AuthProvider';
import WysiwygEditor from '../editor/WysiwygEditor';
import EmailTagsInput from '../inputs/EmailTagsInput';
import '../../styles/ConferenceManager.css';

// Standard icons based on the mock data
const DEFAULT_ICONS = [
  { id: "welcome", name: "Welcome", enabled: true, HTML: "<b>Welcome</b>" },
  { id: "venue-details", name: "Venue Details", enabled: true, HTML: "<b>Venue Details</b>" },
  { id: "health-safety", name: "Health & Safety", enabled: true, HTML: "<b>Health & Safety</b>" },
  { id: "conf-docs", name: "Conf Docs", enabled: true, HTML: "<b>Conference Docs</b>" },
  { id: "soc-reports", name: "SOC Reports", enabled: true, HTML: "<b>SOC Reports</b>" },
  { id: "fringes-timetable", name: "Fringes Timetable", enabled: true, HTML: "<b>Fringes Timetable</b>" },
  { id: "working-together", name: "Working Together", enabled: true, HTML: "<b>Working Together</b>" },
  { id: "other-meetings", name: "Other Meetings", enabled: true, HTML: "<b>Other Meetings</b>" },
  { id: "newssheets", name: "Unison Newssheets", enabled: true, HTML: "<b>Unison Newssheets</b>" },
  { id: "attendees-chat", name: "Attendees Chat", enabled: true, HTML: "<b>Attendees Chat</b>" },
  { id: "exhibitors", name: "Unison Exhibitors", enabled: true, HTML: "<b>Unison Exhibitors</b>" },
  { id: "unizone", name: "Unizone", enabled: true, HTML: "<b>Unizone</b>" },
  { id: "year-of-theme", name: "Year of [Theme]", enabled: true, HTML: "<b>Year of [Theme]</b>" },
  { id: "discounts", name: "Discounts", enabled: true, HTML: "<b>Discounts</b>" },
  { id: "fair-representation", name: "Fair Representation", enabled: true, HTML: "<b>Fair Representation</b>" },
  { id: "solidarity-stands", name: "Solidarity Stands", enabled: true, HTML: "<b>Solidarity Stands</b>" },
  { id: "ocs-howto", name: "OCS How-To", enabled: true, HTML: "<b>OCS How-To</b>" },
  { id: "follow-us", name: "Follow Us", enabled: true, HTML: "<b>Follow Us</b>" }
];


// Define interfaces for schedule events
interface ScheduleEvent {
  id: string;
  title: string;
  startTime: string; // Format: HH:MM
  endTime: string;   // Format: HH:MM
  location: string;
  date: string;      // Format: YYYY-MM-DD
  description?: string;
  type?: string;     // For color coding (e.g., "break", "workshop", "ceremony")
}

interface Icon {
  id: string;
  name: string;
  enabled: boolean;
  HTML: string;
}

interface ConferenceData {
  name: string;
  data: {
    id?: string;
    enabled?: string | boolean;
    name?: string;
    startDate?: any; // Using any to handle various date formats
    endDate?: any; // Using any to handle various date formats
    venue?: any;
    icons?: Icon[];
    schedule?: ScheduleEvent[];
    moderators?: string[]; // Array of moderator emails
    bannedChatUsers?: string[]; // Array of banned user emails
    chatDisclaimer?: string; // Text for chat disclaimer
    featuredImageUrl?: string; // URL for home screen image
    [key: string]: any;
  };
  lastUpdated: number;
}

interface MetadataField {
  id: string;
  name: string;
  enabled?: boolean;
}

interface MetadataCollection {
  permissions: {
    read: string | string[];
    write: string | string[];
  };
  fields: MetadataField[];
}

interface ConferenceManagerProps {
  db: Firestore;
  functions: Functions;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
}

// Type guard for Firestore Timestamp
function isFirestoreTimestamp(value: any): boolean {
  return value && 
    typeof value === 'object' && 
    (
      // Check for toDate method (Firestore SDK object)
      (typeof value.toDate === 'function') ||
      // Or check for seconds and nanoseconds (serialized timestamp from cache)
      ('seconds' in value && 'nanoseconds' in value)
    );
}

const ConferenceManager: React.FC<ConferenceManagerProps> = ({ 
  db, 
  functions, 
  onError, 
  onSuccess, 
  setLoading 
}) => {
  const { currentUser } = useAuth();
  const [conferences, setConferences] = useState<MetadataField[]>([]);
  const [selectedConference, setSelectedConference] = useState<string | null>(null);
  const [conferenceData, setConferenceData] = useState<ConferenceData | null>(null);
  const [formData, setFormData] = useState<{
    enabled: string;
    name: string;
    startDate: string;
    endDate: string;
    venue: string;
    venueAddress: string;
    venueDetails: {
      refreshments: boolean;
      refreshmentsDetails: string;
      wifi: boolean;
      wifiNetwork: string;
      wifiPassword: string;
      accessibility: boolean;
      accessibilityDetails: string;
      additionalInfo: string;
    };
    moderators: string[];
    bannedChatUsers: string[]; 
    chatDisclaimer: string;
    chatEnabled: boolean; // Added for chat enabled status
    moderatorOnlyChat: boolean; // Added for moderator-only chat mode
    featuredImageUrl: string;
  }>({
    enabled: 'true',
    name: '',
    startDate: '',
    endDate: '',
    venue: '',
    venueAddress: '',
    venueDetails: {
      refreshments: false,
      refreshmentsDetails: '',
      wifi: false,
      wifiNetwork: '',
      wifiPassword: '',
      accessibility: false,
      accessibilityDetails: '',
      additionalInfo: ''
    },
    moderators: [],
    bannedChatUsers: [], 
    chatDisclaimer: '',
    chatEnabled: true, // Default to enabled
    moderatorOnlyChat: false, // Default to all users can chat
    featuredImageUrl: ''
  });
  
  const [icons, setIcons] = useState<Icon[]>(DEFAULT_ICONS);
  const [selectedIconIndex, setSelectedIconIndex] = useState<number | null>(null);
  const [newConferenceName, setNewConferenceName] = useState<string>('');
  const [showNewConferenceForm, setShowNewConferenceForm] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'list' | 'edit'>('list');
  
  // Schedule management state
  const [schedule, setSchedule] = useState<ScheduleEvent[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [showEventForm, setShowEventForm] = useState<boolean>(false);
  const [editingEvent, setEditingEvent] = useState<ScheduleEvent>({
    id: '',
    title: '',
    startTime: '09:00',
    endTime: '10:00',
    location: '',
    date: '',
  });
  
  // Helper function to format date values for the form inputs without timezone info
  const formatDateForInput = (dateValue: any): string => {
    if (!dateValue) return '';
    
    // Handle Firestore Timestamp with toDate method
    if (isFirestoreTimestamp(dateValue)) {
      if (typeof dateValue.toDate === 'function') {
        // Live Firestore timestamp - format as YYYY-MM-DD
        const date = dateValue.toDate();
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      } else {
        // Serialized timestamp with seconds/nanoseconds
        const date = new Date(dateValue.seconds * 1000);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      }
    }
    
    // Handle standard JS Date
    if (dateValue instanceof Date) {
      return `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}-${String(dateValue.getDate()).padStart(2, '0')}`;
    }
    
    // Handle string dates
    if (typeof dateValue === 'string') {
      try {
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        }
      } catch (e) {
        console.error('Error parsing date string:', e);
        return '';
      }
    }
    
    console.log('Unhandled date format:', dateValue);
    return '';
  };

  // Schedule helper functions
  const getDatesArray = (startDateStr: string, endDateStr: string): string[] => {
    if (!startDateStr || !endDateStr) return [];
    
    const startDate = new Date(startDateStr.split('T')[0]);
    const endDate = new Date(endDateStr.split('T')[0]);
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return [];
    
    const dates: string[] = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  const formatDateTab = (dateStr: string, index: number): string => {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Invalid';
    
    const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' };
    return `Day ${index + 1}: ${date.toLocaleDateString('en-UK', options)}`;
  };

  const formatFullDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      timeZone: 'UTC'
    };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (timeStr: string): string => {
    // Convert 24-hour time to 12-hour time with AM/PM
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const getEventsForDate = (date: string): ScheduleEvent[] => {
    return schedule.filter(event => event.date === date);
  };

  // Cloud function references - updated to use generic functions
  const createDocumentFunc = httpsCallable(functions, 'createDocument');
  const updateDocumentFunc = httpsCallable(functions, 'updateDocument');

  // Load conferences from metadata
  useEffect(() => {
    const loadConferences = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      try {
        const metadataRef = doc(db, 'metadata', 'conferences');
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          const metadataData = metadataSnapshot.data() as MetadataCollection;
          setConferences(metadataData.fields || []);
        } else {
          setConferences([]);
        }
      } catch (err) {
        console.error('Error loading conferences:', err);
        onError('Failed to load conferences. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadConferences();
  }, [db, currentUser, setLoading, onError]);

  // Load conference data when selected
  useEffect(() => {
    const loadConferenceData = async () => {
      if (!selectedConference) {
        setConferenceData(null);
        setFormData({
          enabled: 'true',
          name: '',
          startDate: '',
          endDate: '',
          venue: '',
          venueAddress: '',
          venueDetails: {
            refreshments: false,
            refreshmentsDetails: '',
            wifi: false,
            wifiNetwork: '',
            wifiPassword: '',
            accessibility: false,
            accessibilityDetails: '',
            additionalInfo: ''
          },
          moderators: [],
          bannedChatUsers: [],
          chatDisclaimer: '',
          chatEnabled: true,
          moderatorOnlyChat: false,
          featuredImageUrl: ''
        });
        setIcons(DEFAULT_ICONS); // Reset to default icons
        setSelectedIconIndex(null);
        setSchedule([]); // Reset schedule
        setSelectedDate(''); // Reset selected date
        return;
      }
      
      setLoading(true);
      try {
        const conferenceRef = doc(db, 'conferences', selectedConference);
        const conferenceSnapshot = await getDoc(conferenceRef);
        
        if (conferenceSnapshot.exists()) {
          const data = conferenceSnapshot.data() as ConferenceData;
          setConferenceData(data);
          
          // Initialize form data with timezone-free date handling and venue details
          setFormData({
            enabled: typeof data.data.enabled === 'boolean' 
              ? data.data.enabled.toString() 
              : (data.data.enabled || 'true'),
            name: data.data.name || data.name || '',
            startDate: formatDateForInput(data.data.startDate),
            endDate: formatDateForInput(data.data.endDate),
            venue: data.data.venue?.name || '',
            venueAddress: data.data.venue?.address || '',
            venueDetails: {
              refreshments: data.data.venue?.facilities?.refreshments?.enabled || false,
              refreshmentsDetails: data.data.venue?.facilities?.refreshments?.details || '',
              wifi: data.data.venue?.facilities?.wifi?.enabled || false,
              wifiNetwork: data.data.venue?.facilities?.wifi?.network || '',
              wifiPassword: data.data.venue?.facilities?.wifi?.password || '',
              accessibility: data.data.venue?.facilities?.accessibility?.enabled || false,
              accessibilityDetails: data.data.venue?.facilities?.accessibility?.details || '',
              additionalInfo: data.data.venue?.additionalInfo || ''
            },
            // Initialize fields with data from Firestore (or empty values if not present)
            moderators: data.data.moderators || [],
            bannedChatUsers: data.data.bannedChatUsers || [],
            chatDisclaimer: data.data.chatDisclaimer || '',
            // Load chat settings
            chatEnabled: data.data.chatSettings?.enabled !== undefined ? data.data.chatSettings.enabled : true,
            moderatorOnlyChat: data.data.chatSettings?.mode === 'moderators_only',
            featuredImageUrl: data.data.featuredImageUrl || ''
          });
          
          // Initialize schedule data
          setSchedule(data.data.schedule || []);
          
          // Load icons with proper merging
          if (data.data.icons && Array.isArray(data.data.icons)) {
            // Create a dictionary of icons by ID for easy lookup
            const existingIconsMap: Record<string, Icon> = {};
            data.data.icons.forEach(icon => {
              existingIconsMap[icon.id] = icon;
            });
            
            // Merge with defaults, taking saved values when they exist
            const mergedIcons = DEFAULT_ICONS.map(defaultIcon => {
              if (existingIconsMap[defaultIcon.id]) {
                return existingIconsMap[defaultIcon.id];
              }
              return defaultIcon;
            });
            
            setIcons(mergedIcons);
          } else {
            // If no icons or not an array, use defaults
            setIcons(DEFAULT_ICONS);
          }
          
          setSelectedIconIndex(null);
          
          // Set the selected date to the start date if available
          if (data.data.startDate) {
            const startDateStr = formatDateForInput(data.data.startDate);
            if (startDateStr) {
              setSelectedDate(startDateStr);
            }
          }
          
          setViewMode('edit');
        } else {
          setConferenceData(null);
          onError(`Conference with ID ${selectedConference} not found.`);
        }
      } catch (err) {
        console.error('Error loading conference data:', err);
        onError('Failed to load conference data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadConferenceData();
  }, [db, selectedConference, setLoading, onError]);

  // Handle input changes for conference form
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handler for nested form input changes
  const handleNestedInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      venueDetails: {
        ...prev.venueDetails,
        [name]: value
      }
    }));
  };

  // Handler for checkbox changes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      venueDetails: {
        ...prev.venueDetails,
        [name]: checked
      }
    }));
  };

  // Handler for chat settings checkbox changes
  const handleChatSettingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Handler for moderators tags component
  const handleModeratorsChange = (emails: string[]) => {
    setFormData(prev => ({
      ...prev,
      moderators: emails
    }));
  };
  
  // Handler for banned users tags component
  const handleBannedUsersChange = (emails: string[]) => {
    setFormData(prev => ({
      ...prev,
      bannedChatUsers: emails
    }));
  };

  // Handle icon enabled/disabled toggle
  const handleIconToggle = (index: number) => {
    const updatedIcons = [...icons];
    updatedIcons[index].enabled = !updatedIcons[index].enabled;
    setIcons(updatedIcons);
  };

  // Handle icon selection for editing - improved version
  const handleIconSelect = (index: number) => {
    // Log the current selection for debugging
    console.log(`Selecting icon at index ${index}:`, icons[index]);
    console.log(`Icon HTML content:`, icons[index]?.HTML);
    
    // Set the selected icon index
    setSelectedIconIndex(index);
  };

  // Handle HTML content update for selected icon - improved version
  const handleIconHtmlUpdate = (html: string) => {
    if (selectedIconIndex !== null) {
      // Create a deep copy of the icons array
      const updatedIcons = JSON.parse(JSON.stringify(icons));
      
      // Update the HTML for the selected icon
      updatedIcons[selectedIconIndex].HTML = html;
      
      // Log the update
      console.log(`Updating HTML for icon ${icons[selectedIconIndex].id}`);
      console.log(`New HTML:`, html);
      
      // Update the icons state
      setIcons(updatedIcons);
    }
  };
  
  // Handle icon name update
  const handleIconNameUpdate = (name: string) => {
    if (selectedIconIndex !== null) {
      const updatedIcons = [...icons];
      updatedIcons[selectedIconIndex].name = name;
      setIcons(updatedIcons);
    }
  };

  // Schedule event handlers
  const handleEventInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditingEvent(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // FIXED EVENT SUBMIT FUNCTION
  const handleEventSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    // No need for preventDefault as we're using a button click handler
    
    // Validate event data before proceeding
    if (!editingEvent.title || !editingEvent.startTime || !editingEvent.endTime || !editingEvent.location || !editingEvent.date) {
      onError('Please fill in all required fields: title, time, location, and date.');
      return;
    }
    
    try {
      // Safely clone the schedule array
      const updatedSchedule = [...schedule];
      
      if (editingEvent.id) {
        // Update existing event
        const eventIndex = updatedSchedule.findIndex(event => event.id === editingEvent.id);
        if (eventIndex !== -1) {
          updatedSchedule[eventIndex] = {...editingEvent};
        }
      } else {
        // Add new event with a unique ID
        updatedSchedule.push({
          ...editingEvent,
          id: `event-${Date.now()}`
        });
      }
      
      // First update the schedule
      setSchedule(updatedSchedule);
      
      // After updating state, close the modal
      setShowEventForm(false);
      
      // Clear the editing event form for next use
      setTimeout(() => {
        setEditingEvent({
          id: '',
          title: '',
          startTime: '09:00',
          endTime: '10:00',
          location: '',
          date: selectedDate,
        });
      }, 100);
      
      // Show success message
      onSuccess(`Event "${editingEvent.title}" ${editingEvent.id ? 'updated' : 'added'} successfully.`);
    } catch (err) {
      console.error('Error updating schedule:', err);
      onError('Failed to update the schedule. Please try again.');
    }
  };

  const handleDeleteEvent = (eventId: string, e: React.MouseEvent<HTMLButtonElement>) => {
    // Stop event propagation to prevent the event card from being clicked
    e.stopPropagation();
    
    // Use window.confirm() explicitly or create a custom confirmation
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        const updatedSchedule = schedule.filter(event => event.id !== eventId);
        setSchedule(updatedSchedule);
        onSuccess('Event deleted successfully.');
      } catch (err) {
        console.error('Error deleting event:', err);
        onError('Failed to delete the event. Please try again.');
      }
    }
  };
  
  // Update conference using the generic updateDocument cloud function
  const handleUpdateConference = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedConference || !conferenceData) return;
    
    setLoading(true);
    try {
      // Create dates without timezone information by using specific UTC dates
      let startDate = null;
      let endDate = null;
      
      if (formData.startDate) {
        // Parse the YYYY-MM-DD format and create a date with the time set to noon UTC
        // This helps avoid timezone shift issues when the date is later displayed
        const [year, month, day] = formData.startDate.split('-').map(num => parseInt(num, 10));
        startDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
      }
      
      if (formData.endDate) {
        // Parse the YYYY-MM-DD format and create a date with the time set to noon UTC
        const [year, month, day] = formData.endDate.split('-').map(num => parseInt(num, 10));
        endDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
      }
      
      // Prepare venue data
      const venueData = {
        name: formData.venue,
        address: formData.venueAddress,
        additionalInfo: formData.venueDetails.additionalInfo,
        facilities: {
          refreshments: {
            enabled: formData.venueDetails.refreshments,
            details: formData.venueDetails.refreshmentsDetails
          },
          wifi: {
            enabled: formData.venueDetails.wifi,
            network: formData.venueDetails.wifiNetwork,
            password: formData.venueDetails.wifiPassword
          },
          accessibility: {
            enabled: formData.venueDetails.accessibility,
            details: formData.venueDetails.accessibilityDetails
          }
        }
      };
      
      // Prepare data for the cloud function
      const conferenceData = {
        enabled: formData.enabled === 'true',
        name: formData.name, // Include name directly in the data object
        startDate: startDate,
        endDate: endDate,
        venue: venueData,
        icons: icons,
        schedule: schedule, // Include schedule data
        // Add fields
        moderators: formData.moderators,
        bannedChatUsers: formData.bannedChatUsers,
        chatDisclaimer: formData.chatDisclaimer,
        chatSettings: {
          mode: formData.moderatorOnlyChat ? 'moderators_only' : 'all_can_send',
          enabled: formData.chatEnabled
        },
        featuredImageUrl: formData.featuredImageUrl
      };
      
      // Call the updateDocument cloud function with the simplified parameters
      const result = await updateDocumentFunc({
        collectionName: 'conferences',
        docId: selectedConference,
        data: conferenceData
      });
      
      // @ts-ignore (result data structure)
      if (result.data.success) {
        // @ts-ignore
        onSuccess(`Conference "${formData.name}" updated successfully.`);
        
        // Refresh conference data
        const conferenceRef = doc(db, 'conferences', selectedConference);
        const conferenceSnapshot = await getDoc(conferenceRef);
        if (conferenceSnapshot.exists()) {
          setConferenceData(conferenceSnapshot.data() as ConferenceData);
        }
        
        // Refresh conferences list to update any name changes
        const metadataRef = doc(db, 'metadata', 'conferences');
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          const metadataData = metadataSnapshot.data() as MetadataCollection;
          setConferences(metadataData.fields || []);
        }
        
        // Return to list view
        setViewMode('list');
        setSelectedConference(null);
      } else {
        // @ts-ignore
        onError(result.data.message || 'Failed to update conference.');
      }
    } catch (err: any) {
      console.error('Error updating conference:', err);
      onError(err.message || 'Failed to update conference. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Create new conference
  const handleCreateConference = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newConferenceName.trim()) {
      onError('Conference name is required.');
      return;
    }
    
    setLoading(true);
    try {
      // Generate a simple ID from the name (lowercase, replace spaces with dash)
      const conferenceId = newConferenceName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
      
      // Check if ID is already in use
      const existingConference = conferences.find(conf => conf.id === conferenceId);
      if (existingConference) {
        onError(`A conference with this name already exists. Please use a different name.`);
        setLoading(false);
        return;
      }
      
      // All default icons are enabled with empty HTML content
      const initialIcons = DEFAULT_ICONS.map(icon => ({
        ...icon,
        enabled: true
      }));
      
      // Initial venue data
      const initialVenueData = {
        name: "",
        address: "",
        additionalInfo: "",
        facilities: {
          refreshments: { enabled: false, details: "" },
          wifi: { enabled: false, network: "", password: "" },
          accessibility: { enabled: false, details: "" }
        }
      };
      
      // Call cloud function to create document in both metadata and collection
      const result = await createDocumentFunc({
        collectionName: 'conferences',
        docId: conferenceId,
        docName: newConferenceName,
        data: {
          id: conferenceId,
          enabled: true,
          name: newConferenceName,
          startDate: null,
          endDate: null,
          venue: initialVenueData,
          icons: initialIcons,
          schedule: [],
          moderators: [],
          bannedChatUsers: [],
          chatDisclaimer: "",
          chatSettings: {
            mode: 'all_can_send', // Default to all users can send messages
            enabled: true // Default to chat enabled
          },
          featuredImageUrl: ""
        }
      });
      
      // @ts-ignore (result data structure)
      if (result.data.success) {
        onSuccess(`Conference "${newConferenceName}" created successfully.`);
        
        // Refresh conferences list
        const metadataRef = doc(db, 'metadata', 'conferences');
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          const metadataData = metadataSnapshot.data() as MetadataCollection;
          setConferences(metadataData.fields || []);
        }
        
        // Reset form
        setNewConferenceName('');
        setShowNewConferenceForm(false);
      } else {
        // @ts-ignore
        onError(result.data.message || 'Failed to create conference.');
      }
    } catch (err: any) {
      console.error('Error creating conference:', err);
      onError(err.message || 'Failed to create conference. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle back to list view
  const handleBackToList = () => {
    setViewMode('list');
    setSelectedConference(null);
  };

  // FIXED EVENT FORM MODAL
  const renderEventFormModal = () => {
    if (!showEventForm) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>{editingEvent.id ? 'Edit Event' : 'Add New Event'}</h3>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowEventForm(false)}
            >
              <span className="icon-close">✖️</span>
            </button>
          </div>
          
          <div className="event-form">
            <div className="form-field">
              <label>Event Title</label>
              <input 
                type="text"
                name="title"
                value={editingEvent.title}
                onChange={handleEventInputChange}
                placeholder="Enter event title"
              />
            </div>
            
            <div className="form-row">
              <div className="form-field">
                <label>Start Time</label>
                <input 
                  type="time"
                  name="startTime"
                  value={editingEvent.startTime}
                  onChange={handleEventInputChange}
                />
              </div>
              <div className="form-field">
                <label>End Time</label>
                <input 
                  type="time"
                  name="endTime"
                  value={editingEvent.endTime}
                  onChange={handleEventInputChange}
                />
              </div>
            </div>
            
            <div className="form-field">
              <label>Location</label>
              <input 
                type="text"
                name="location"
                value={editingEvent.location}
                onChange={handleEventInputChange}
                placeholder="Enter event location"
              />
            </div>
            
            <div className="form-field">
              <label>Description (Optional)</label>
              <textarea
                name="description"
                value={editingEvent.description || ''}
                onChange={handleEventInputChange}
                placeholder="Enter event description"
                rows={3}
              />
            </div>
            
            <div className="form-field">
              <label>Event Type (for color coding)</label>
              <select
                name="type"
                value={editingEvent.type || ''}
                onChange={handleEventInputChange}
              >
                <option value="">Default</option>
                <option value="break">Break</option>
                <option value="workshop">Workshop</option>
                <option value="ceremony">Ceremony</option>
                <option value="panel">Panel</option>
              </select>
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="save-btn"
                onClick={handleEventSubmit}
              >
                {editingEvent.id ? 'Update Event' : 'Add Event'}
              </button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => setShowEventForm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render the conference list view
  const renderConferenceList = () => (
    <div className="conference-list-view">
      <div className="view-header">
        <h2>Conference Management</h2>
        <button
          type="button"
          className="add-conference-btn"
          onClick={() => setShowNewConferenceForm(!showNewConferenceForm)}
        >
          {showNewConferenceForm ? 'Cancel' : '+ Add Conference'}
        </button>
      </div>
      
      {showNewConferenceForm && (
        <div className="new-conference-form">
          <h3>Create New Conference</h3>
          <form onSubmit={handleCreateConference}>
            <div className="form-field">
              <label>Conference Name</label>
              <input
                type="text"
                value={newConferenceName}
                onChange={(e) => setNewConferenceName(e.target.value)}
                placeholder="Enter conference name"
                required
              />
            </div>
            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-btn"
                disabled={!newConferenceName.trim()}
              >
                Create Conference
              </button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => setShowNewConferenceForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      <div className="conference-cards">
        {conferences.length > 0 ? (
          conferences.map(conference => (
            <div
              key={conference.id}
              className="conference-card"
            >
              <div className="card-content">
                <h3 className="card-title">{conference.name}</h3>
                <div className="card-status">
                  <span className={`status-indicator ${conference.enabled ? 'enabled' : 'disabled'}`}>
                    {conference.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="card-footer">
                  <button 
                    type="button"
                    className="edit-btn" 
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event bubbling
                      setSelectedConference(conference.id);
                    }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>No conferences available. Create your first conference to get started.</p>
          </div>
        )}
      </div>
    </div>
  );

  // The final return statement with the complete UI
  return (
    <div className="conference-manager">
      {viewMode === 'list' ? renderConferenceList() : (
        <div className="conference-edit-view">
          <div className="view-header">
            <button 
              type="button"
              className="back-btn" 
              onClick={handleBackToList}
            >
              ← Back to Conferences
            </button>
            <h2>Edit Conference: {conferenceData?.name}</h2>
          </div>
          
          <form onSubmit={handleUpdateConference} className="edit-conference-form">
            <div className="form-section">
              <h3>Conference Details</h3>
              
              <div className="form-field">
                <label>Status</label>
                <select
                  name="enabled"
                  value={formData.enabled}
                  onChange={handleInputChange}
                  className="status-select"
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>
              
              <div className="form-field">
                <label>Conference Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter conference name"
                  required
                />
              </div>
              
              <div className="form-row">
                <div className="form-field">
                  <label>Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-field">
                  <label>End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              {/* Home Screen Image URL field */}
              <div className="form-field">
                <label>Home Screen Image URL</label>
                <input
                  type="text"
                  name="featuredImageUrl"
                  value={formData.featuredImageUrl}
                  onChange={handleInputChange}
                  placeholder="Enter URL for home screen featured image"
                />
                <small className="helper-text">This image will be displayed on the conference home screen</small>
              </div>
            </div>
            
            {/* Chat Settings Section */}
            <div className="form-section">
              <h3>Chat Settings</h3>
              <p className="section-description">
                Configure chat moderation and disclaimer settings
              </p>
              
              {/* Add these checkboxes for chat settings */}
              <div className="form-field">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="chatEnabled"
                    checked={formData.chatEnabled}
                    onChange={handleChatSettingChange}
                  />
                  <span>Enable Chat</span>
                </label>
                <small className="helper-text">When disabled, users won't be able to send messages in the chat.</small>
              </div>
              
              <div className="form-field">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="moderatorOnlyChat"
                    checked={formData.moderatorOnlyChat}
                    onChange={handleChatSettingChange}
                  />
                  <span>Moderator-Only Chat</span>
                </label>
                <small className="helper-text">When enabled, only moderators can send messages in the chat.</small>
              </div>
              
              <div className="form-field">
                <label>Chat Moderators</label>
                <EmailTagsInput 
                  emails={formData.moderators}
                  onChange={handleModeratorsChange}
                  placeholder="Add moderator email..."
                />
                <small className="helper-text">Add email addresses for chat moderators. Press Enter, comma, or space after each email.</small>
              </div>
              
              <div className="form-field">
                <label>Chat Banned Users</label>
                <EmailTagsInput 
                  emails={formData.bannedChatUsers}
                  onChange={handleBannedUsersChange}
                  placeholder="Add banned user email..."
                />
                <small className="helper-text">Add email addresses for users banned from chat. Press Enter, comma, or space after each email.</small>
              </div>
              
              <div className="form-field">
                <label>Chat Disclaimer</label>
                <textarea
                  name="chatDisclaimer"
                  value={formData.chatDisclaimer}
                  onChange={handleInputChange}
                  placeholder="Enter chat disclaimer text"
                  rows={4}
                  className="form-textarea"
                />
                <small className="helper-text">This text will be displayed to users before they can access the chat</small>
              </div>
            </div>
            
            {/* Venue Details Section */}
            <div className="form-section">
              <h3>Venue Details</h3>
              <p className="section-description">
                Provide information about the conference venue
              </p>
              
              <div className="form-field">
                <label>Venue Name</label>
                <input
                  type="text"
                  name="venue"
                  value={formData.venue}
                  onChange={handleInputChange}
                  placeholder="Enter venue name"
                />
              </div>
              
              <div className="form-field">
                <label>Venue Address</label>
                <input
                  type="text"
                  name="venueAddress"
                  value={formData.venueAddress}
                  onChange={handleInputChange}
                  placeholder="Enter full venue address"
                />
              </div>
              
              <div className="form-field">
                <label>Additional Venue Information</label>
                <textarea
                  name="additionalInfo"
                  value={formData.venueDetails.additionalInfo}
                  onChange={handleNestedInputChange}
                  placeholder="Additional information about the venue (optional)"
                  rows={3}
                  className="form-textarea"
                />
              </div>
              
              <div className="venue-facilities">
                <h4>Venue Facilities</h4>
                
                <div className="facility-item">
                  <div className="facility-header">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="refreshments"
                        checked={formData.venueDetails.refreshments}
                        onChange={handleCheckboxChange}
                      />
                      <span>Refreshments</span>
                    </label>
                  </div>
                  
                  {formData.venueDetails.refreshments && (
                    <div className="facility-details">
                      <input
                        type="text"
                        name="refreshmentsDetails"
                        value={formData.venueDetails.refreshmentsDetails}
                        onChange={handleNestedInputChange}
                        placeholder="Refreshments details (e.g. 'Available in the Main Lobby & Floor 2')"
                      />
                    </div>
                  )}
                </div>
                
                <div className="facility-item">
                  <div className="facility-header">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="wifi"
                        checked={formData.venueDetails.wifi}
                        onChange={handleCheckboxChange}
                      />
                      <span>Wi-Fi</span>
                    </label>
                  </div>
                  
                  {formData.venueDetails.wifi && (
                    <div className="facility-details">
                      <div className="form-row">
                        <div className="form-field">
                          <label>Network Name</label>
                          <input
                            type="text"
                            name="wifiNetwork"
                            value={formData.venueDetails.wifiNetwork}
                            onChange={handleNestedInputChange}
                            placeholder="Wi-Fi network name"
                          />
                        </div>
                        <div className="form-field">
                          <label>Password</label>
                          <input
                            type="text"
                            name="wifiPassword"
                            value={formData.venueDetails.wifiPassword}
                            onChange={handleNestedInputChange}
                            placeholder="Wi-Fi password"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="facility-item">
                  <div className="facility-header">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="accessibility"
                        checked={formData.venueDetails.accessibility}
                        onChange={handleCheckboxChange}
                      />
                      <span>Accessibility</span>
                    </label>
                  </div>
                  
                  {formData.venueDetails.accessibility && (
                    <div className="facility-details">
                      <input
                        type="text"
                        name="accessibilityDetails"
                        value={formData.venueDetails.accessibilityDetails}
                        onChange={handleNestedInputChange}
                        placeholder="Accessibility details (e.g. 'All areas are wheelchair accessible')"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Schedule Management Section */}
            <div className="form-section">
              <h3>Conference Schedule</h3>
              <p className="section-description">
                Manage the conference schedule by adding events to each day
              </p>

              <div className="schedule-management">
                <div className="schedule-dates">
                  {getDatesArray(formData.startDate, formData.endDate).map((date, index) => (
                    <button
                      type="button"
                      key={date}
                      className={`date-tab ${selectedDate === date ? 'active' : ''}`}
                      onClick={() => setSelectedDate(date)}
                    >
                      {formatDateTab(date, index)}
                    </button>
                  ))}
                  
                  {(!formData.startDate || !formData.endDate) && (
                    <div className="empty-schedule-message">
                      <span className="icon-calendar">📅</span>
                      <p>Set conference start and end dates to manage the schedule</p>
                    </div>
                  )}
                </div>

                {selectedDate && (
                  <div className="schedule-events-container">
                    <div className="schedule-header">
                      <h4>{formatFullDate(selectedDate)}</h4>
                      <button
                        type="button"
                        className="add-event-btn"
                        onClick={() => {
                          setEditingEvent({
                            id: '',
                            title: '',
                            startTime: '09:00',
                            endTime: '10:00',
                            location: '',
                            date: selectedDate,
                          });
                          setShowEventForm(true);
                        }}
                      >
                        <span className="icon-add">➕</span>
                        Add Event
                      </button>
                    </div>

                    <div className="schedule-events-list">
                      {getEventsForDate(selectedDate).length > 0 ? (
                        getEventsForDate(selectedDate)
                          .sort((a, b) => a.startTime.localeCompare(b.startTime))
                          .map((event) => (
                            <div 
                              key={event.id} 
                              className={`schedule-event-item ${event.type || ''}`}
                              onClick={() => {
                                setEditingEvent(event);
                                setShowEventForm(true);
                              }}
                            >
                              <div className="event-time">
                                {formatTime(event.startTime)} - {formatTime(event.endTime)}
                              </div>
                              <div className="event-details">
                                <h5>{event.title}</h5>
                                <p>{event.location}</p>
                              </div>
                              <button
                                type="button"
                                className="delete-event-btn"
                                onClick={(e) => handleDeleteEvent(event.id, e)}
                              >
                                <span className="icon-delete" style={{ color: '#ff6b6b' }}>🗑️</span>
                              </button>
                            </div>
                          ))
                      ) : (
                        <div className="empty-events-message">
                          <p>No events scheduled for this day. Click "Add Event" to create one.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Icons Section */}
            <div className="form-section">
              <h3>Conference Icons</h3>
              <p className="section-description">
                Select which icons to display and edit their content
              </p>
              
              <div className="icons-editor">
                <div className="icons-list-panel">
                  <div className="icons-list">
                    {icons.map((icon, index) => (
                      <div
                        key={icon.id}
                        className={`icon-item ${selectedIconIndex === index ? 'selected' : ''}`}
                        onClick={() => handleIconSelect(index)}
                      >
                        <div 
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            width: '100%',
                            cursor: 'pointer'
                          }} 
                        >
                          <input
                            type="checkbox"
                            checked={icon.enabled}
                            onChange={() => handleIconToggle(index)}
                            style={{
                              flexShrink: 0,
                              margin: 0,
                              cursor: 'pointer'
                            }}
                          />
                          <span 
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {icon.name}
                          </span>
                        </div>

                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="icon-edit-panel">
                  {selectedIconIndex !== null ? (
                    <div className="icon-editor">
                      <div className="editor-header">
                        <h4>Editing: {icons[selectedIconIndex].name}</h4>
                        <div className="icon-name-field">
                          <label>Icon Name</label>
                          <input
                            type="text"
                            value={icons[selectedIconIndex].name}
                            onChange={(e) => handleIconNameUpdate(e.target.value)}
                            placeholder="Enter icon name"
                          />
                        </div>
                      </div>
                      
                      <div className="icon-content-field">
                        <label>Icon Content</label>
                        <WysiwygEditor
                          key={`icon-editor-${selectedIconIndex}`}
                          initialHtml={icons[selectedIconIndex]?.HTML || ''}
                          onChange={handleIconHtmlUpdate}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="no-icon-selected">
                      <p>Select an icon from the list to edit its content.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-btn">Save Changes</button>
              <button type="button" className="cancel-btn" onClick={handleBackToList}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Render the modal outside the form to avoid form submission conflicts */}
      {renderEventFormModal()}
    </div>
  );
};

export default ConferenceManager;